// Cookie/privacy consent utilities. Pure client-side, SSR-safe.
import { supabase } from "@/integrations/supabase/client";
import { detectCountry, getCachedCountry, isGdprCountry } from "@/lib/geo";


export type ConsentPrefs = {
  necessary: true;
  analytics: boolean;
  functional: boolean;
  advertising: boolean;
  decidedAt: string;
  expiresAt: string;
  gpc?: boolean;
};

const KEY = "cws-consent-v1";
export const OPEN_PREFS_EVENT = "cws:open-cookie-prefs";
export const CONSENT_CHANGED_EVENT = "cws:consent-changed";
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export const DEFAULT_PREFS = {
  necessary: true as const,
  analytics: false,
  functional: false,
  advertising: false,
};

export function isGpcEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  // @ts-ignore - non-standard
  return navigator.globalPrivacyControl === true;
}

export function getConsent(): ConsentPrefs | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentPrefs;
    // Treat expired consent as undecided
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return { ...DEFAULT_PREFS, ...parsed, necessary: true };
  } catch {
    return null;
  }
}

export function hasDecided(): boolean {
  return getConsent() !== null;
}

async function logConsent(prefs: ConsentPrefs) {
  try {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const uaHash = await sha256(ua);
    const locale = typeof navigator !== "undefined" ? sanitizeLocale(navigator.language) : null;
    await supabase.from("consent_log").insert({
      decided_at: prefs.decidedAt,
      choices: {
        analytics: prefs.analytics,
        functional: prefs.functional,
        advertising: prefs.advertising,
      },
      gpc: !!prefs.gpc,
      user_agent_hash: uaHash,
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      locale,
    });
  } catch { /* noop */ }
}

function sanitizeLocale(locale: string): string | null {
  const normalized = locale.replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 20);
  return normalized || null;
}

async function sha256(input: string): Promise<string | null> {
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return null;
    const buf = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
  } catch { return null; }
}

export function setConsent(prefs: Partial<Omit<ConsentPrefs, "decidedAt" | "expiresAt" | "necessary">>): ConsentPrefs {
  const current = getConsent();
  const now = new Date();
  const merged: ConsentPrefs = {
    necessary: true,
    analytics: prefs.analytics ?? current?.analytics ?? false,
    functional: prefs.functional ?? current?.functional ?? false,
    advertising: prefs.advertising ?? current?.advertising ?? false,
    decidedAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + ONE_YEAR_MS).toISOString(),
    gpc: prefs.gpc ?? isGpcEnabled(),
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(merged));
      window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: merged }));
    } catch { /* noop */ }
    void logConsent(merged);
  }
  return merged;
}

export function acceptAll() {
  return setConsent({ analytics: true, functional: true, advertising: true });
}

export function rejectNonEssential() {
  return setConsent({ analytics: false, functional: false, advertising: false });
}

export function applyGpcIfNeeded(): boolean {
  // If GPC active and user hasn't decided yet, auto-store rejection.
  if (isGpcEnabled() && !hasDecided()) {
    rejectNonEssential();
    return true;
  }
  return false;
}

/**
 * Default consent behavior: if the visitor hasn't explicitly decided and
 * Global Privacy Control is NOT active, opt them in to all categories.
 * Returns true if a default was applied (so the UI can suppress any banner).
 *
 * Legal note: this is acceptable under California's CCPA/CPRA (opt-out model)
 * and still honors GPC. Not GDPR-compliant for EU visitors.
 */
export function autoAcceptIfUndecided(): boolean {
  if (hasDecided()) return false;
  if (isGpcEnabled()) {
    rejectNonEssential();
    return true;
  }
  acceptAll();
  return true;
}

/**
 * Region-aware default. EU/UK visitors get the opt-in banner (no auto-accept);
 * everyone else is auto-opted-in (CCPA-style). GPC always wins.
 */
export async function applyRegionalConsentDefault(): Promise<string | null> {
  if (hasDecided()) return getCachedCountry();
  if (isGpcEnabled()) {
    rejectNonEssential();
    return getCachedCountry();
  }
  const cached = getCachedCountry();
  if (cached && !isGdprCountry(cached)) {
    acceptAll();
    return cached;
  }
  const country = await detectCountry();
  if (!country) {
    // Network failed — fall back to auto-accept so analytics keeps working.
    acceptAll();
    return null;
  }
  if (!isGdprCountry(country)) acceptAll();
  return country;
}

/** Clears stored consent so the visitor is treated as new on next load. */
export function resetConsent() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: null }));
  } catch { /* noop */ }
}

export function openPreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_PREFS_EVENT));
}

export function analyticsAllowed(): boolean {
  return getConsent()?.analytics === true;
}
export function functionalAllowed(): boolean {
  return getConsent()?.functional === true;
}
export function advertisingAllowed(): boolean {
  return getConsent()?.advertising === true;
}
